import LoginExpiry from "@curefit/pandora/dist/components/loginBoundary/loginExpiry.js";
import { trackConfig } from "./AppConstants";
import { EventNames } from "./EventTrackerConstants";
import FetchHelper from "./FetchHelper";
import { isEmpty as _isEmpty } from "lodash";
import RollbarUtils from "./RollbarUtils";
import { Tracker } from "./Tracker";
import { ApiManager } from "../api/ApiManager";
import AppUtils from "./AppUtils";


export default class ApiUtils {

    public static apiGetRequest<T>(url: string, supressRollbar?: boolean, timeoutInSeconds?: number): Promise<any> {
        let didTimeOut = false;
        return new Promise(function (resolve: any, reject: any) {
            let timeout: any;
            let currTime = new Date().getTime();
            if (timeoutInSeconds !== undefined && !isNaN(timeoutInSeconds)) {
                timeout = setTimeout(function () {
                    didTimeOut = true;
                    let error = new Error("Request timed out");
                    RollbarUtils.sendErrorToRollbar(error, { "url": url });
                    reject(error);
                }, timeoutInSeconds * 1000);
            }

            fetch(url, {
                credentials: "same-origin",
                headers: {
                    ...FetchHelper.getHeaders(url,AppUtils.getTenant())
                }
            }).then((response: any) => {
                const trackService = trackConfig.servicesToTrack.find((item: string) => url.indexOf(item) > -1);
                let difference = new Date().getTime() - currTime;
                if (trackService && trackService.length > 0 && difference > (trackConfig.maxLatencyAllowedInSec * 1000)) {
                    Tracker.track(EventNames.Slow_Connection, { latency: difference })
                }
                // Clear the timeout as cleanup
                clearTimeout(timeout);
                if (!didTimeOut) {
                    if (response.status >= 200 && response.status < 300) {
                        response.json().then((data: any) => {
                            resolve(data);
                        });
                    }
                    else if (response.status === 302) {
                        this.showLoginBoundaryModal(url, FetchHelper.getHeaders(url,AppUtils.getTenant()), "GET", response.headers, response.status);
                    }
                    else {
                        if (response.headers.get("content-type") && response.headers.get("content-type").includes("application/json") && response.status !== 401) {
                            response.json().then((data: any) => {
                                if (!supressRollbar) RollbarUtils.sendErrorToRollbar(new Error(`Fetch failed at ${data.path} with status ${data.status}`), {
                                    "response": data
                                });
                                reject(data);
                            })
                        } else if (response.status !== 401) {
                            if (!supressRollbar) {
                                RollbarUtils.sendErrorToRollbar(new Error(`Fetch Failed at ${url}`), {});
                            }
                            reject();
                        } else if (response.status === 401) {
                            window.location.href = `${location.protocol}//${window.location.host}/login?next=${window.location.pathname}`;
                        }
                        if (!supressRollbar)
                            console.error("Response code 200 expected, while received: " + response.status);
                        else
                            console.log("Response code 200 expected, while received: " + response.status);

                    }
                }
            }).catch((error: any) => {
                if (url.includes("/dummy/track")) {
                    // ApiUtils.showLoginBoundaryModal(url, FetchHelper.getHeaders(url,AppUtils.getTenant()), "GET");
                }
                // Rejection already happened with setTimeout
                if (didTimeOut) return;
                if (!supressRollbar)
                    console.error("Error while hitting the GET Request on URL: " + url, error);
                else
                    console.log("Error while hitting the GET Request on URL: " + url, error);
            });
        });
    }

    public static apiHeadRequest<T>(url: string, supressRollbar?: boolean, timeoutInSeconds?: number): Promise<any> {
        let didTimeOut = false;
        return new Promise(function (resolve: any, reject: any) {
            let timeout: any;
            let currTime = new Date().getTime();
            if (timeoutInSeconds !== undefined && !isNaN(timeoutInSeconds)) {
                timeout = setTimeout(function () {
                    didTimeOut = true;
                    let error = new Error("Request timed out");
                    RollbarUtils.sendErrorToRollbar(error, { "url": url });
                    reject(error);
                }, timeoutInSeconds * 1000);
            }

            fetch(url, {
                credentials: "same-origin",
                method: "HEAD",
                headers: {
                    ...FetchHelper.getHeaders(url,AppUtils.getTenant())
                }
            }).then((response: any) => {
                const trackService = trackConfig.servicesToTrack.find((item: string) => url.indexOf(item) > -1);
                let difference = new Date().getTime() - currTime;
                if (trackService && trackService.length > 0 && difference > (trackConfig.maxLatencyAllowedInSec * 1000)) {
                    Tracker.track(EventNames.Slow_Connection, { latency: difference })
                }
                // Clear the timeout as cleanup
                clearTimeout(timeout);
                if (!didTimeOut) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(true);
                    } else if (response.status === 302) {
                        this.showLoginBoundaryModal(url, FetchHelper.getHeaders(url,AppUtils.getTenant()), "HEAD", response.headers, response.status);
                    } else if (response.headers.get("content-type") && response.headers.get("content-type").includes("application/json") && response.status !== 401) {
                        if (!supressRollbar) RollbarUtils.sendErrorToRollbar(new Error(`Fetch failed at ${response.path} with status ${response.status}`), {
                            "response": response
                        });
                        reject(false);
                    } else if (response.status !== 401) {
                        if (!supressRollbar) {
                            RollbarUtils.sendErrorToRollbar(new Error(`Fetch Failed at ${url}`), {});
                        }
                        reject(false);
                    } else if (response.status === 401) {
                        window.location.href = `${location.protocol}//${window.location.host}/login?next=${window.location.pathname}`;
                        reject(false);
                    }
                }
            }).catch((error: any) => {
                // Rejection already happened with setTimeout
                if (didTimeOut) return;
                if (!supressRollbar)
                    console.error("Error while hitting the HEAD Request on URL: " + url, error);
                else
                    console.log("Error while hitting the HEAD Request on URL: " + url, error);
                reject(false);
            });
        });
    }

    public static apiPostRequest(url: string, jsonBody: string, headers?: any, supressRollbar?: boolean, timeoutInSeconds?: number): Promise<any> {
        let didTimeOut = false;
        return new Promise(function (resolve: any, reject: any) {
            let timeout: any;
            if (timeoutInSeconds !== undefined && !isNaN(timeoutInSeconds)) {
                timeout = setTimeout(function () {
                    didTimeOut = true;
                    let error = new Error("Request timed out");
                    RollbarUtils.sendErrorToRollbar(error, { "url": url, "request": jsonBody });
                    reject(error);
                }, timeoutInSeconds * 1000);
            }

            fetch(url, {
                credentials: "same-origin",
                method: "POST",
                headers: {
                    ...FetchHelper.getHeaders(),
                    ...(headers || {}),
                    "Content-Type": "application/json"
                },
                body: jsonBody
            }).then((response: any) => {
                clearTimeout(timeout);
                if (!didTimeOut) {
                    if (response.status >= 200 && response.status < 300) {
                        response.json().then((data: any) => {
                            resolve(data);
                        });
                    } else if (response.status === 302) {
                        this.showLoginBoundaryModal(url, { ...FetchHelper.getHeaders(url,AppUtils.getTenant()), ...headers }, "POST", response.headers, response.status);
                    } else if (response.headers.get("content-type") && response.headers.get("content-type").includes("application/json") && response.status !== 401) {
                        response.json().then((data: any) => {
                            if (!supressRollbar) RollbarUtils.sendErrorToRollbar(new Error(`Fetch failed at ${data.path} with status ${data.status}`), {
                                "response": data
                            });
                            reject(data);
                        })
                    } else if (!supressRollbar) {
                        RollbarUtils.sendErrorToRollbar(new Error(`Fetch Failed at ${url} with status ${response.status}`), {});
                    }
                }
            }).catch((error: any) => {
                if (didTimeOut) return; // Rejection already happened with setTimeout
                if (!supressRollbar)
                    console.error("Error while hitting POST request on URL: " + url, error);
                else
                    console.log("Error while hitting POST request on URL: " + url, error);

                if (url === "/orion/plan/savePatientPlanInDraft") {
                    ApiUtils.showLoginBoundaryModal(url, FetchHelper.getHeaders(url,AppUtils.getTenant()), "POST")
                }
            });
        });
    }

    public static apiDeleteRequest(url: string, jsonBody: string, supressRollbar?: boolean, timeoutInSeconds?: number): Promise<any> {
        let didTimeOut = false;
        return new Promise((resolve: any, reject: any) => {
            let timeout: any;
            if (timeoutInSeconds !== undefined && !isNaN(timeoutInSeconds)) {
                timeout = setTimeout(function () {
                    didTimeOut = true;
                    let error = new Error("Request timed out");
                    RollbarUtils.sendErrorToRollbar(error, { "url": url, "request": jsonBody });
                    reject(error);
                }, timeoutInSeconds * 1000);
            }

            fetch(url, {
                credentials: "same-origin",
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json"
                },
                body: jsonBody
            }).then((response: any) => {
                clearTimeout(timeout);
                if (!didTimeOut) {
                    if (response.status >= 200 && response.status < 300) {
                        response.json().then((data: any) => {
                            resolve(data);
                        });
                    } else {
                        response.json().then((data: any) => {
                            if (!supressRollbar) RollbarUtils.sendErrorToRollbar(new Error("Fetch[POST] failed"), { "url": url, "request": jsonBody, "response": data, "status": response.status });
                            reject(data);
                        });
                        if (!supressRollbar)
                            console.error("Response code 200 expected, while received: " + response.status);
                        else
                            console.log("Response code 200 expected, while received: " + response.status);

                    }
                }
            }).catch((error: any) => {
                if (didTimeOut) return; // Rejection already happened with setTimeout
                if (!supressRollbar)
                    console.error("Error while hitting POST request on URL: " + url, error);
                else
                    console.log("Error while hitting POST request on URL: " + url, error);
            });
        });
    }


    public static apiPutRequest(url: string, jsonBody: string): Promise<any> {
        return new Promise((resolve: any, reject: any) => {
            fetch(url, {
                credentials: "same-origin",
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                body: jsonBody
            }).then((response: any) => {
                if (response.status >= 200 && response.status < 300) {
                    response.json().then((data: any) => {
                        resolve(data);
                    });
                } else {
                    reject(response.json());
                    console.log("Error code 200 expected, while received: " + response.status);
                }
            }).catch((error: any) => {
                console.log("Error while hitting PUT request on URL: " + url, error);
            });
        });
    }

    public static apiFormDataRequest(url: string, data: Blob, fileMimeType: string, cb?: (loaded: number, total: number) => void) {
        return new Promise((resolve: any, reject: any) => {
            // let formData = new FormData();
            // formData.append("file", file);

            let request = new XMLHttpRequest();
            request.open("PUT", url, true);
            request.setRequestHeader('Content-Type', fileMimeType)

            request.upload.addEventListener("progress", (event: any) => {
                console.log({ loaded: event.loaded, total: event.total })
                cb && cb(event.loaded, event.total)
                if (event.lengthComputable) {
                }
            })

            request.addEventListener("load", () => {
                resolve(request.responseText)
            })

            request.addEventListener("error", (event) => {
                console.log({ event: request.responseText })
                reject()
            })

            request.addEventListener("abort", (event) => {
                console.log({ abort: request.responseText })
                reject()
            })

            request.send(data)

            // fetch(url, {
            //     credentials: "same-origin",
            //     method: "POST",
            //     body: formData
            // }).then((response: any) => {
            //     if (response.status >= 200 && response.status < 300) {
            //         if (!_isEmpty(response)) {
            //             response.json().then((data: any) => {
            //                 resolve(data);
            //             });
            //         } else {
            //             resolve();
            //         }
            //     } else {
            //         reject(response.json());
            //         console.log("Error code 200 expected, while received: " + response.status);
            //     }
            // }).catch((error: any) => {
            //     console.log("Error while hitting PUT request on URL: " + url, error);
            // });
        })
    }

    public static createApiUrl(apiUrlSuffix: string, host: string = undefined, port: string = undefined): string {
        if (host && port && host.length > 0 && port.length > 0) {
            return host + ":" + port + apiUrlSuffix;
        } else {
            return apiUrlSuffix;
        }
    }

    public static requestToQueryParams(reqParams: any, hyphenated: boolean = false): string {
        let currQueryParams = [];
        for (let key in reqParams) {
            if (key in reqParams && reqParams[key] != null) {
                let changedKey = key;
                if (hyphenated) {
                    changedKey = key.replace(/[\w]([A-Z])/g, (m) => {
                        return m[0] + "-" + m[1];
                    }).toLowerCase();
                }
                currQueryParams.push(changedKey + "=" + reqParams[key]);
            }
        }
        return currQueryParams.join("&");
    }

    public static showLoginBoundaryModal(url: string, requestHeaders: any, method: string, responseHeaders?: any, status?: string): void {     // shows loginBoundary modal when status code is 302 and there are no cookies present
        const details = JSON.stringify({ url, requestHeaders, method, responseHeaders, status });
        ApiManager.getOrionApis().logLogoutStatus(details);
        LoginExpiry.show({
            title: "Login Expired",
            description: "Login expired, you need to login again to continue.",
        });
        // LoginExpiry.hide();
        // try {
        //     window.alert("You have been logged out. Click OK to refresh and login again")
        RollbarUtils.sendErrorToRollbar(new Error(`Received 403` + url), details)
        //     window.location.reload()
        // } catch (e) {
        //     console.log({e})
        // }
    }

    public static getFilterData = (nameKey: String, myArray: any[]) => {
        if (nameKey) {
            for (let i = 0; i < myArray.length; i++) {
                if (myArray[i].name === nameKey) {
                    return myArray[i];
                }
            }
        }
        else return "";

    }
}